import { ObjectId } from '@freightview/object-id'
import { omit, sum } from 'lodash-es'

import type {
  ClientPackagingType,
  FullShipment,
  LegacyHandlingUnitDTO,
  LoadItem,
  ProductCatalogEntry,
} from '@fv/client-types'
import type {
  FreightClass,
  HazardSubClass,
  LoadItem as LoadItemModel,
  LoadItemRefNum,
  PackingGroup,
  Workflow,
} from '@fv/models'
import type { DTO } from '@fv/models/core'

import type { ClientDTO } from '@/types'
import type {
  ApiLoadContainedItemDTO,
  ApiLoadHandlingUnitDTO,
  ApiLoadHandlingUnitHazardDTO,
} from '@/types/api-dto'

import {
  buildLegacyHandlingUnitDTO,
  displayNmfc,
  normalizePackageType,
  parseNmfc,
} from '../commodityUtils'
import { useHandlingUnits } from '../useHandlingUnits'
import type {
  LoadHandlingUnitFormModel,
  LoadItemFormModel,
  LoadStep,
} from './types'
import { shouldRequireDims } from './useLoadHandlingUnitSchema'

export const catalogToItemData = (
  data: ProductCatalogEntry,
): LoadHandlingUnitFormModel => {
  // TODO
  // the product catalog convert legacy handling units (product catalog units) to "ProductCatalogEntry"
  // which is shouldn't need to do.  Because there are already several places where we're trying to convert product catalog entries to a load handling unit or load item or back to a product catlog unit
  const {
    hazard,
    hazardous,
    declaredValue,
    saidToContain,
    saidToContainPackagingType,
    ...handlingUnit
  } = buildLegacyHandlingUnitDTO(data)

  return {
    ...omit(handlingUnit, '_id', 'mode', 'autoGenerated'),
    handlingUnitId: handlingUnit._id,
    _id: new ObjectId().toHexString(),
    type: handlingUnit.package ?? undefined,
    declaredValue: declaredValue?.amount,
    quantity: handlingUnit.pieces,
    fullNmfc: handlingUnit.nmfc,
    isHazardous: hazardous,
    ...((saidToContain || saidToContainPackagingType) && {
      contains: [
        {
          _id: new ObjectId().toHexString(),
          quantity: saidToContain,
          type: saidToContainPackagingType,
        },
      ],
    }),
    ...(hazard && {
      hazardous: {
        hazmatId: hazard?.hazmatId,
        packingGroup: hazard?.packingGroup,
        permitNumber: hazard?.specialPermit,
        primaryClass: hazard?.hazardClass,
        secondaryClass: hazard?.secondaryHazardClass,
        tertiaryClass: hazard?.tertiaryHazardClass,
        properShippingName: hazard?.properShippingName,
        technicalName: hazard?.technicalName,
        reportableQuantity: hazard?.reportableQuantity,
        residueLastContained: hazard?.residueLastContained,
        limitedQuantity: hazard?.limitedQuantity,
        marinePollutant: hazard?.marinePollutant,
      },
    }),
  }
}

export const toLoadItemFormModel = (
  item: LoadItem,
  unit?: LegacyHandlingUnitDTO,
): LoadItemFormModel => {
  return {
    ...item,
    fullNmfc: displayNmfc(item),
    isHazardous: !!item.hazardous?.length,
    hazardous: item.hazardous?.[0],
    ...(unit?.readOnly && {
      readOnly: unit.readOnly,
    }),
  }
}

export const toLoadHandlingUnitFormModel = (
  item: DTO<LoadItemModel>,
  unit?: LegacyHandlingUnitDTO,
): LoadHandlingUnitFormModel => {
  return {
    ...toLoadItemFormModel(item, unit),
    contains: item.contains?.map(c => toLoadItemFormModel(c)),
    handlingUnitType: (item.contains?.length ?? 0) > 1 ? 'multi' : 'single',
  }
}

export const defaultLoadContainsItem = (): LoadItemFormModel => ({
  _id: new ObjectId().toHexString(),
  isHazardous: false,
})

export const defaultLoadHandlingUnit = (
  workflow: Workflow,
): LoadHandlingUnitFormModel => ({
  isHazardous: false,
  type: defaultPackagingType(workflow),
  _id: new ObjectId().toHexString(),
  handlingUnitType: 'single',
})

export function isValidItem(
  i: LoadHandlingUnitFormModel,
  workflow: Workflow,
  step: LoadStep,
  accountRequiresDims = false,
  accountRequiresNmfc = false,
) {
  const isMulti = i.handlingUnitType === 'multi'
  const needsQuantity = workflow === 'ltl' && !isMulti
  const needsWeight = !isMulti
  if ((!i.quantity && needsQuantity) || (needsWeight && !i.weight)) {
    return false
  }

  const hasMissingDims = !i.length || !i.width || !i.height

  if (
    shouldRequireDims(i, { requireDims: accountRequiresDims }) &&
    hasMissingDims
  ) {
    return false
  }

  if (accountRequiresNmfc && (isMulti ? !i.contains?.length : !i.fullNmfc)) {
    return false
  }

  if (
    isMulti &&
    i.contains?.some(c => !isValidCommodity(c, step, accountRequiresNmfc))
  ) {
    return false
  }

  return true
}

export const isValidCommodity = (
  i: LoadItemFormModel,
  step: LoadStep,
  requireNmfc = false,
) => {
  const hasWeightAndQuantity = !!i.weight && !!i.quantity
  if (requireNmfc && !i.fullNmfc) {
    return false
  }
  if (step === 'quoting') {
    return hasWeightAndQuantity
  } else {
    const hasHazard = !!i.hazardous?.hazmatId && !!i.hazardous?.primaryClass
    return hasWeightAndQuantity && (!i.isHazardous || hasHazard)
  }
}

const buildHazardousDTO = (
  model: LoadItemFormModel,
): ApiLoadHandlingUnitHazardDTO | undefined => {
  if (!model.isHazardous) {
    return undefined
  }
  if (model.hazardous === undefined || !Object.keys(model.hazardous).length) {
    return {}
  }

  const {
    hazmatId,
    packingGroup,
    permitNumber,
    primaryClass,
    secondaryClass,
    tertiaryClass,
    properShippingName,
    technicalName,
    reportableQuantity,
    residueLastContained,
    limitedQuantity,
    marinePollutant,
  } = model.hazardous || {}

  return {
    ...(hazmatId && { hazmatId }),
    ...(packingGroup && { packingGroup: packingGroup as PackingGroup }),
    ...(permitNumber && { permitNumber }),
    ...(primaryClass && { primaryClass: primaryClass as HazardSubClass }),
    ...(secondaryClass && { secondaryClass: secondaryClass as HazardSubClass }),
    ...(tertiaryClass && { tertiaryClass: tertiaryClass as HazardSubClass }),
    ...(properShippingName && { properShippingName }),
    ...(technicalName && { technicalName }),
    ...(reportableQuantity && { reportableQuantity }),
    ...(residueLastContained && { residueLastContained }),
    ...(limitedQuantity && { limitedQuantity }),
    ...(marinePollutant && { marinePollutant }),
  }
}

const buildContains = (
  model: LoadHandlingUnitFormModel,
  workflow: Workflow,
): ApiLoadHandlingUnitDTO['contains'] | undefined => {
  if (!model.contains?.length) {
    return undefined
  }
  return model.contains.map(c => {
    const itemModel = buildLoadItemDTO(c, workflow)
    return {
      ...itemModel,
    }
  })
}

export const buildLoadItemDTO = (
  model: LoadItemFormModel,
  workflow: Workflow,
): ApiLoadContainedItemDTO => {
  const hazardous = buildHazardousDTO(model)
  let normalizedModel = omit(
    model,
    'fullNmfc',
    'isHazardous',
    'hazardous',
    'refNums',
  )
  return {
    ...normalizedModel,
    type: normalizePackageType(model.type ?? defaultPackagingType(workflow)),
    ...parseNmfc(model.fullNmfc),
    ...(hazardous && { hazardous: [hazardous] }),
    ...(model.refNums?.length && {
      refNums: model.refNums
        .filter(r => !!r.type)
        .map(r => r as LoadItemRefNum),
    }),
    description: model.description ?? '',
    weightUOM: model.weight ? 'lbs' : undefined,
    ...(workflow === 'parcel' && {
      quantity: 1,
      freightClass: undefined,
    }),
    ...(workflow !== 'parcel' && {
      freightClass: model.freightClass as FreightClass,
    }),
  }
}

export const buildHandlingUnitDTO =
  (workflow: Workflow) =>
  (model: ClientDTO<LoadHandlingUnitFormModel>): ApiLoadHandlingUnitDTO => {
    const contains = buildContains(model, workflow)
    const itemModel = buildLoadItemDTO(model, workflow)
    return {
      ...itemModel,
      description: itemModel.description ?? '',
      _id: model._id || new ObjectId().toHexString(),
      quantity: itemModel.quantity ?? 1,
      dropLocationSequence: model.dropLocationSequence ?? 1,
      pickupLocationSequence: model.pickupLocationSequence ?? 0,
      type: normalizePackageType(model.type ?? defaultPackagingType(workflow)),
      contains,
      weight: handlingUnitWeight(model),
      weightUOM: 'lbs',
    }
  }

export const defaultPackagingType = (
  workflow: Workflow,
): ClientPackagingType => (workflow === 'parcel' ? 'custom' : 'pallet')

export const handlingUnitWeight = (unit: LoadHandlingUnitFormModel): number => {
  if (unit.handlingUnitType === 'single') {
    return unit.weight ?? 0
  }
  return sum(unit.contains?.map(c => c.weight ?? 0))
}

export const unitIsHazardous = (unit: LoadHandlingUnitFormModel) =>
  unit.isHazardous || unit.contains?.some(c => c.isHazardous)

export const useLoadHandlingUnitFormModels = (load: FullShipment) => {
  const handlingUnitsQuery = useHandlingUnits(load)
  return {
    isLoading: handlingUnitsQuery.isLoading,
    data: handlingUnitsQuery.isLoading
      ? undefined
      : (load?.items?.map(i =>
          toLoadHandlingUnitFormModel(
            i,
            handlingUnitsQuery.data?.find(h => h._id === i.handlingUnitId),
          ),
        ) ?? []),
  }
}

export type ItemSummaryInput =
  | ApiLoadHandlingUnitDTO
  | ReturnType<typeof buildLoadItemDTO>

export function buildItemSummaryData(
  item: LoadItemFormModel | LoadHandlingUnitFormModel,
  workflow: Workflow,
): ItemSummaryInput {
  if ('handlingUnitType' in item) {
    return buildHandlingUnitDTO(workflow)(item)
  }

  return buildLoadItemDTO(item, workflow)
}
